import type { RoomDocument } from '../api/types';

/**
 * The data room document list's sort (owner, 2026-10-15: "there is no sorting
 * by dates"): by the DATE OF DATA (the end of the period a document covers), by
 * when it was added, or by name. The sort lives in the URL — `?sort=dataDate&dir=desc`
 * — so Back and reload keep it, and it is not a filter: "Show all documents"
 * leaves it alone.
 *
 * Client-side on purpose: the listing is the room's complete, permission-scoped
 * set (never paginated) and already carries `dataDate` and `createdAt`.
 */
export type DocumentSortKey = 'name' | 'dataDate' | 'uploaded';
export type SortDirection = 'asc' | 'desc';

export interface DocumentSort {
  key: DocumentSortKey;
  dir: SortDirection;
}

export const DOCUMENT_SORT_KEYS: readonly DocumentSortKey[] = ['dataDate', 'uploaded', 'name'];

/** The list's order when the URL names none: newest additions first — the
 * listing's own order (search results keep their best-match order instead). */
export const DEFAULT_DOCUMENT_SORT: DocumentSort = { key: 'uploaded', dir: 'desc' };

/** Where a column starts when first chosen: dates newest first, names A–Z. */
export function defaultSortDirection(key: DocumentSortKey): SortDirection {
  return key === 'name' ? 'asc' : 'desc';
}

/** The sort after choosing `key`: the same column flips; another starts at its default. */
export function nextDocumentSort(current: DocumentSort | null, key: DocumentSortKey): DocumentSort {
  if (current?.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: defaultSortDirection(key) };
}

/** The sort named in a query string, or null (none, or not one we know). A
 * known key without a valid direction takes the key's default direction. */
export function documentSortFrom(search: URLSearchParams): DocumentSort | null {
  const raw = search.get('sort');
  const key = DOCUMENT_SORT_KEYS.find((k) => k === raw);
  if (!key) return null;
  const dirRaw = search.get('dir');
  const dir: SortDirection = dirRaw === 'asc' || dirRaw === 'desc' ? dirRaw : defaultSortDirection(key);
  return { key, dir };
}

/** `base` with `sort` written into it (or removed, for null); other keys kept. */
export function withDocumentSort(
  sort: DocumentSort | null,
  base: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  const next = new URLSearchParams(base);
  next.delete('sort');
  next.delete('dir');
  if (sort) {
    next.set('sort', sort.key);
    next.set('dir', sort.dir);
  }
  return next;
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

type Sortable = Pick<RoomDocument, 'name' | 'dataDate' | 'createdAt'>;

const byName = (a: Sortable, b: Sortable) => collator.compare(a.name ?? '', b.name ?? '');
const time = (d: Sortable) => {
  const t = Date.parse(d.createdAt);
  return Number.isNaN(t) ? 0 : t;
};

/**
 * `docs` in `sort` order (a new array). Documents with no date of data sort
 * LAST in either direction — "oldest first" never opens on a wall of undated
 * files. Documents with the same date of data fall back to the name (A–Z);
 * any other tie keeps the order given (the listing's: newest additions first).
 */
export function sortDocuments<T extends Sortable>(docs: readonly T[], sort: DocumentSort): T[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  const compare = (a: T, b: T): number => {
    if (sort.key === 'dataDate') {
      const da = a.dataDate ?? null;
      const db = b.dataDate ?? null;
      if (da && !db) return -1;
      if (!da && db) return 1;
      if (da && db && da !== db) return da < db ? -sign : sign;
      return byName(a, b);
    }
    if (sort.key === 'uploaded') {
      const diff = time(a) - time(b);
      return diff === 0 ? 0 : diff < 0 ? -sign : sign;
    }
    return sign * byName(a, b);
  };
  // Array#sort is stable: equal documents stay in the order given.
  return [...docs].sort(compare);
}
